import { useCommonChartViewData, VisaTimePeriodFilter } from '@va/dashboard/components';
import { useReportBlockNodeBuilder } from '@va/dashboard/shared/report-block-nodes';
import { ReportBlockComparisonTooltip } from '@va/dashboard/ui/components';
import { useDataView } from '@va/dashboard/util-hooks';
import { CompareIcon } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { AddFilterButton, AppliedFilters, useFiltersContext } from '@va/shared/feature-filters';
import { DataViewOption, DataViewToggle, ReportBlockNodes } from '@va/types/report-block';
import { TimePeriod } from '@va/types/time';
import { BaseTimeseriesResponse } from '@va/types/time-series';
import { ExpandableCard, ExpandButton, useExpandableCardContext } from '@va/ui/cards';
import {
  DateComparisonPeriod,
  ReportBlockBody,
  ReportBlockHeader,
  ReportBlockNodeIds,
  ReportBlockNumericHeading,
  ReportBlockProvider,
  ReportBlockSubHeader,
  useReportBlockCtx,
} from '@va/ui/components/report-block';
import { ReportBlockNodeButton } from '@va/ui/components/report-block/nodes/ReportBlockNodeButton';
import { ReportBlockNodesTooltip } from '@va/ui/components/report-block/nodes/ReportBlockNodesTooltip';
import { ReportBlockHeaderProvider } from '@va/ui/components/report-block/report-block-header-ctx';
import { fontWeights, LineChart, Paragraph } from '@va/ui/design-system';
import { addNumberSeparator } from '@va/util/helpers';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { SWRResponse } from 'swr';

type VisitorStatsReportProps = {
  id: string;
  gridPosition: number;
  canExpand?: boolean;
  fetchCurrentHook: (unit?: TimePeriod) => SWRResponse<BaseTimeseriesResponse<any>, Error>;
  fetchPreviousHook: (
    activeComparisonOption: DateComparisonPeriod,
    unit?: TimePeriod,
  ) => SWRResponse<BaseTimeseriesResponse<any>, Error>;
  title: string;
  titleTooltip: string;
  valueMapper?: (total: number) => any;
  /**
   * Optional mapper used only for the numeric heading (falls back to `valueMapper`)
   */
  headingValueMapper?: (total: number) => any;
  reversePercentageColors?: boolean;
  yAxisTickCallback?: (tickValue: string | number) => string;
  showTimePeriodFilter?: boolean;
};

export const VisitorStatsReport = ({ id, gridPosition, ...rest }: VisitorStatsReportProps) => {
  return (
    <ExpandableCard id={id} gridPosition={gridPosition}>
      <VisitorStatsReportContent id={id} {...rest} />
    </ExpandableCard>
  );
};

const VisitorStatsReportContent = ({
  id,
  canExpand = false,
  fetchCurrentHook,
  fetchPreviousHook,
  title,
  titleTooltip,
  valueMapper,
  headingValueMapper,
  reversePercentageColors = false,
  yAxisTickCallback,
  showTimePeriodFilter,
}: Omit<VisitorStatsReportProps, 'gridPosition'>) => {
  const translate = useTranslate();
  const buildReportBlockNode = useReportBlockNodeBuilder();
  const { expanded } = useExpandableCardContext();
  const { appliedFilters } = useFiltersContext();

  const showAppliedFilters = !canExpand || expanded;
  const hasAppliedFilters = appliedFilters.length > 0;

  const nodes = useMemo<ReportBlockNodes>(
    () => ({
      compareTo: buildReportBlockNode(
        ReportBlockNodeIds.COMPARE_TO,
        canExpand && !expanded ? { headerComponent: <VisitorStatsComparisonToggle /> } : undefined,
      ),
      filterButton: showAppliedFilters
        ? buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON)
        : hasAppliedFilters
          ? buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON, {
              headerComponent: (
                <AddFilterButton disabled disabledTooltip={translate('card.kpiReportBlock.filtersCollapsedTooltip')} />
              ),
            })
          : undefined,
      appliedFilters: showAppliedFilters ? <AppliedFilters /> : undefined,
    }),
    [buildReportBlockNode, canExpand, expanded, showAppliedFilters, hasAppliedFilters, translate],
  );

  const visualization = useDataView({
    defaultView: DataViewOption.lineDiagram,
    identifier: id,
    options: [
      {
        view: DataViewOption.lineDiagram,
        options: [DataViewOption.lineDiagram],
        toggles: [
          { id: DataViewToggle.displayTrendLine },
          { id: DataViewToggle.displayDataPoints },
          { id: DataViewToggle.highlightMinMax },
        ],
      },
    ],
  });

  return (
    <ReportBlockProvider id={id} title={title} titleTooltip={titleTooltip} visualization={visualization} nodes={nodes}>
      <SmallLineChartRbBody
        id={id}
        useCurrentData={fetchCurrentHook}
        fetchPreviousHook={fetchPreviousHook}
        reversePercentageColors={reversePercentageColors}
        valueMapper={valueMapper}
        headingValueMapper={headingValueMapper}
        yAxisTickCallback={yAxisTickCallback}
        showTimePeriodFilter={showTimePeriodFilter}
        showExpandButton={canExpand}
        canExpand={canExpand}
        isExpandedDesktop={canExpand && expanded}
        showTrendLine={visualization.getToggleState?.(DataViewToggle.displayTrendLine)}
        showDataLabels={visualization.getToggleState?.(DataViewToggle.displayDataPoints)}
        highlightMinMax={visualization.getToggleState?.(DataViewToggle.highlightMinMax)}
      />
    </ReportBlockProvider>
  );
};

const VisitorStatsComparisonToggle = () => {
  const { showPrevious } = useReportBlockCtx();
  const translate = useTranslate();

  return (
    <ReportBlockNodesTooltip
      content={
        <>
          <div className='p-2 mb-1'>
            <Paragraph variant='body' variantSize='large' weight={fontWeights.medium}>
              {translate('button.compareTo')}
            </Paragraph>
          </div>
          <ReportBlockComparisonTooltip />
        </>
      }
    >
      <div>
        <ReportBlockNodeButton
          icon={<CompareIcon color={showPrevious ? '#FFFFFF' : undefined} />}
          className={showPrevious ? '!bg-green-haze' : undefined}
        />
      </div>
    </ReportBlockNodesTooltip>
  );
};

const SmallLineChartRbBody = ({
  id,
  useCurrentData,
  fetchPreviousHook,
  reversePercentageColors,
  valueMapper,
  headingValueMapper,
  yAxisTickCallback,
  showTimePeriodFilter,
  showExpandButton,
  canExpand,
  isExpandedDesktop,
  showTrendLine,
  showDataLabels,
  highlightMinMax,
}: {
  id: string;
  useCurrentData: (unit?: TimePeriod) => SWRResponse<BaseTimeseriesResponse<string>, Error>;
  fetchPreviousHook: (
    activeComparisonOption: DateComparisonPeriod,
    unit?: TimePeriod,
  ) => SWRResponse<BaseTimeseriesResponse<string>, Error>;
  reversePercentageColors?: boolean;
  valueMapper?: (total: number) => any;
  headingValueMapper?: (total: number) => any;
  yAxisTickCallback?: (tickValue: string | number) => string;
  showTimePeriodFilter?: boolean;
  showExpandButton?: boolean;
  canExpand?: boolean;
  isExpandedDesktop?: boolean;
  showTrendLine?: boolean;
  showDataLabels?: boolean;
  highlightMinMax?: boolean;
}) => {
  const { locale } = useLocale();
  const { showPrevious, activeComparisonOption } = useReportBlockCtx();

  const [timePeriod, setTimePeriod] = useState<TimePeriod>(TimePeriod.day);
  const effectiveUnit = showTimePeriodFilter ? timePeriod : undefined;

  const getTooltipValueFunc = useCallback(
    (val: any) => (valueMapper ? valueMapper(val) : addNumberSeparator(val, locale)),
    [locale, valueMapper],
  );

  const { chartData, tooltipFn, isLoading, tiles } = useCommonChartViewData<'line'>({
    useCurrentData: () => useCurrentData(effectiveUnit),
    usePreviousData: () => fetchPreviousHook(activeComparisonOption, effectiveUnit),
    datasetKeys: [id],
    getTooltipValueFunc,
    reversePercentageColors,
    showPrevious,
  });

  const headingValue = useMemo(() => Number(tiles[0]?.headingValue ?? 0), [tiles]);
  const percentage = useMemo(() => Number(tiles[0]?.percentageChange ?? 0), [tiles]);

  const mapHeadingValue = headingValueMapper ?? valueMapper;

  return (
    <ReportBlockBody className='h-full flex flex-col'>
      <ReportBlockHeaderProvider>
        <ReportBlockHeader />
        <ReportBlockSubHeader />
      </ReportBlockHeaderProvider>
      <ReportBlockNumericHeading
        percentageChange={percentage}
        value={mapHeadingValue ? mapHeadingValue(headingValue) : addNumberSeparator(headingValue, locale)}
        reversePercentageColors={reversePercentageColors}
        isLoading={isLoading}
      />
      {showTimePeriodFilter && (
        <VisaTimePeriodFilter
          selectedTimePeriod={timePeriod}
          onChange={setTimePeriod}
          hideTitle
          shortFormat
          smallButtons
        />
      )}

      <div
        className={classNames(
          'flex-1 relative',
          isExpandedDesktop ? 'min-h-[42rem]' : canExpand ? 'min-h-[10rem]' : 'min-h-[14rem]',
        )}
      >
        <div className='absolute inset-0'>
          <LineChart
            isLoading={isLoading}
            tooltipFn={tooltipFn}
            id={`${id}-line-diagram-canvas`}
            chartData={chartData}
            className='h-full'
            configOptions={{
              showTrendLine,
              showDataLabels,
              highlightMinMax,
              datalabelsValueFormatter: getTooltipValueFunc,
              yAxisTickCallback,
            }}
          />
        </div>
      </div>
      {showExpandButton && <ExpandButton />}
    </ReportBlockBody>
  );
};
